/**
 * Industry loss multipliers: IBM Cost of a Data Breach 2026, industry average ÷ global average,
 * rounded to 0.05 (Roger R3, 2026-11-02; IBM edition and the defense/government rows, Pat
 * 2026-11-03).
 *
 * ⚠ A COPY of backend/src/services/lossEventConstants.json, which is the source. The frontend
 * cannot import across the package boundary, so `lossEventConstants.parity.test.ts` reads the
 * backend file and fails if these values or aliases drift from it.
 */
export const INDUSTRY_LOSS_SECTORS: Record<string, { multiplier: number; aliases: string[] }> = {
  healthcare: { multiplier: 1.35, aliases: ['healthcare', 'health care'] },
  financial: { multiplier: 1.25, aliases: ['finance', 'financial', 'financial services', 'banking', 'fintech'] },
  industrial: { multiplier: 1.10, aliases: ['industrial', 'manufacturing'] },
  technology: { multiplier: 1.10, aliases: ['technology', 'tech', 'software'] },
  energy: { multiplier: 1.05, aliases: ['energy'] },
  public_sector: {
    multiplier: 0.70,
    aliases: ['government', 'public sector', 'federal government', 'state government', 'local government'],
  },
};

/** Risk Aperture classifications onto an IBM sector (IBM publishes no figure of their own). */
export const INDUSTRY_LOSS_CLASSIFICATIONS: Record<string, { sector: string; aliases: string[] }> = {
  defense: {
    sector: 'industrial',
    aliases: ['defense', 'defence', 'defense & aerospace', 'aerospace & defense', 'aerospace and defense',
      'defense and aerospace', 'defense industrial base', 'dib'],
  },
};

export const INDUSTRY_LOSS_DEFAULT_MULTIPLIER = 1.0;

const ALIAS_TO_SECTOR = new Map<string, string>();
for (const [key, s] of Object.entries(INDUSTRY_LOSS_SECTORS)) s.aliases.forEach((a) => ALIAS_TO_SECTOR.set(a, key));
for (const c of Object.values(INDUSTRY_LOSS_CLASSIFICATIONS)) c.aliases.forEach((a) => ALIAS_TO_SECTOR.set(a, c.sector));

/** IBM loss multiplier for an industry answer; case- and whitespace-insensitive. Unknown → 1.0. */
export function industryLossMultiplier(industry: string | null | undefined): number {
  if (typeof industry !== 'string') return INDUSTRY_LOSS_DEFAULT_MULTIPLIER;
  const sector = ALIAS_TO_SECTOR.get(industry.trim().toLowerCase());
  return sector ? INDUSTRY_LOSS_SECTORS[sector].multiplier : INDUSTRY_LOSS_DEFAULT_MULTIPLIER;
}

export interface AnnualLossEventTier {
  label: string;
  minRevenueUsd: number;
  probability: number;
  extrapolated: boolean;
}

/**
 * IRIS 2022 (Cyentia, CISA-sponsored) annual probability of a loss event, by revenue tier.
 * ⚠ A COPY of annualLossEventProbability.tiers in backend/src/services/lossEventConstants.json;
 * the parity test checks it.
 */
export const ANNUAL_LOSS_EVENT_TIERS: AnnualLossEventTier[] = [
  { label: 'under $10M', minRevenueUsd: 0, probability: 0.1153, extrapolated: true },
  { label: '$10M-$100M', minRevenueUsd: 10000000, probability: 0.1153, extrapolated: false },
  { label: '$100M-$1B', minRevenueUsd: 100000000, probability: 0.1295, extrapolated: false },
  { label: '$1B-$10B', minRevenueUsd: 1000000000, probability: 0.1704, extrapolated: false },
  { label: '$10B-$100B', minRevenueUsd: 10000000000, probability: 0.2193, extrapolated: false },
  { label: 'over $100B', minRevenueUsd: 100000000000, probability: 0.2933, extrapolated: false },
];

/** Annual loss-event probability for a revenue in USD; null unless revenue is a finite number > 0. */
export function annualLossEventProbability(
  revenueUsd: number | null | undefined,
): { probability: number; tier: string; extrapolated: boolean } | null {
  if (typeof revenueUsd !== 'number' || !Number.isFinite(revenueUsd) || revenueUsd <= 0) return null;
  let hit = ANNUAL_LOSS_EVENT_TIERS[0];
  for (const t of ANNUAL_LOSS_EVENT_TIERS) if (revenueUsd >= t.minRevenueUsd) hit = t;
  return { probability: hit.probability, tier: hit.label, extrapolated: hit.extrapolated };
}
